import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getEngagement, listEngagementResponses, submitEngagementResponse } from '../../../api';
import { useAlerts } from '../../../alerts';
import { Button, EncouragementNote, EngagementQuestionForm } from '../../../components';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Markdown from 'react-markdown';

const Challenge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setError, clearErrors } = useAlerts();

  const [engagement, setEngagement] = useState(null);
  const [engagementResponses, setEngagementResponses] = useState([]);
  const [inResponseMode, setInResponseMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showEncouragementNote, setShowEncouragementNote] = useState(false);

  useEffect(() => {
    getEngagement(id).then(engagement => {
      setEngagement(engagement);
    }).catch(error => {
      setError(error ?? 'Failed to load engagement');
    });

    refreshResponses();

    return () => clearErrors();
  }, [id]);

  const refreshResponses = () => {
    listEngagementResponses(id).then(responses => {
      setEngagementResponses(responses);
    }).catch(error => {
      setError(error ?? 'Failed to load engagement responses');
    });
  }

  const onSubmit = (questionResponses) => {
    setIsLoading(true);

    submitEngagementResponse(id, questionResponses).then(() => {
      setShowEncouragementNote(true);
      setInResponseMode(false);
    }).catch(error => {
      setError(error ?? 'Failed to submit survey');
    }).finally(() => setIsLoading(false));
  }

  const onCloseEncouragementNote = () => {
    if (engagement.getMaxSubmissions() - engagementResponses.length <= 1) {
      navigate('/home');
    } else {
      setShowEncouragementNote(false);
      refreshResponses();
    }
  }

  return (
    <div>
      {/* Encouragement Note */}
      <EncouragementNote
        isVisible={showEncouragementNote}
        onClose={onCloseEncouragementNote}>
        <Markdown>{engagement?.getEncouragementNote()}</Markdown>
      </EncouragementNote>

      {inResponseMode ? (
        <div>
          {/* Back to Lesson Button */}
          <Button secondary onClick={() => setInResponseMode(false)} style={{ marginBottom: '1em' }}>
            <ChevronLeftIcon style={{ color: 'white', fontSize: 15 }} />
            Back to Lesson
          </Button>

          {/* Engagement Questions */}
          <EngagementQuestionForm
            engagementQuestions={engagement?.getQuestions()}
            onSubmit={onSubmit}
            isLoading={isLoading}
            challengePrompt={engagement?.getChallengePrompt()}
          />
        </div>
      ) : (
        <div>
          <Typography variant='h1' gutterBottom>{engagement?.getTitle()}</Typography>

          {/* Lesson */}
          {engagement?.getLesson() &&
          <Markdown style={{ marginBottom: 20 }}>{engagement.getLesson()}</Markdown>}

          {/* Video */}
          {engagement?.getVideoUrl() &&
          <video
            width="100%"
            controls
            style={{ borderRadius: '8px' }}
          >
            <source src={engagement.getVideoUrl()} type="video/mp4" />
            Your browser does not support the video tag.
          </video>}

          {/* Event Responses */}
          {engagementResponses.map((response, index) => (
            <div key={index} style={{ backgroundColor: '#f0f0f0', padding: '20px', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant='body1' style={{ textAlign: 'center' }}>Logged interaction on {response.getCreatedAt()?.toLocaleString()}</Typography>
            </div>
          ))}

          {/* Additional Response Button */}
          {engagementResponses.length < engagement?.getMaxSubmissions() && (
            <Button
              secondary={engagementResponses.length > 0}
              fullWidth
              style={{ marginTop: '1em' }}
              onClick={() => setInResponseMode(true)}>
              {engagementResponses.length === 0 ? 'Start Your Challenge' : '[Optional] Replay Challenge with Another Partner'}
            </Button>
          )}

          <Button
            secondary={engagementResponses.length === 0}
            component={Link}
            to='/home'
            style={{ marginTop: '1em' }}
            fullWidth>
            {engagementResponses.length === 0 ? 'Back to All Engagements' : 'Done'}
          </Button>
        </div>
      )}
    </div>
  );
}
export default Challenge;